import { useState, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { EVENT_NAME } from 'common/constants';
import { useAmplitude } from 'components/AmplitudeProvider';
import { ScrollToTopButton } from 'components/ScrollToTopButton/ScrollToTopButton';
import { useFlagValue } from 'hooks';
import { HighVolumePrintChecksTable } from 'pages/HighVolumeLandingPage/components/HighVolumePrintChecksTable';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import { amplitudeTracker } from 'utils/AmplitudeTracker';
import {
  PrintChecksDialog,
  PrintFailureDialog,
  PrintSuccessDialog
} from '../dialogs';
import { PrintChecksTable } from './PrintChecksTable';
import {
  clearCheckWritingResponse,
  getIsPrintChecksDialogOpen,
  getIsPrintFailureDialogOpen,
  getIsPrintSuccessDialogOpen,
  setIsPrintFailureDialogOpen,
  getPrintChecksData,
  getIsWorkflowPending,
  setPrintChecksData
} from './slice';

export const PrintChecks = (): React.JSX.Element => {
  const amplitude = useAmplitude();
  const isPrintChecksDialogOpen = useAppSelector(getIsPrintChecksDialogOpen);
  const isPrintSuccessDialogOpen = useAppSelector(getIsPrintSuccessDialogOpen);
  const isPrintFailureDialogOpen = useAppSelector(getIsPrintFailureDialogOpen);
  const isWorkflowPending = useAppSelector(getIsWorkflowPending);
  const printChecksData = useAppSelector(getPrintChecksData);
  const landingPageFlag = useFlagValue('dmsAccountingLandingPage');
  const [trackLaunchOfPrintChecks, setTrackLaunchOfPrintChecks] =
    useState(false);

  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (
      !isPrintChecksDialogOpen &&
      !printChecksData.length &&
      !isPrintSuccessDialogOpen &&
      !isPrintFailureDialogOpen
    )
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      queryClient.invalidateQueries(['printAuthorizedVendors']);
  }, [
    isPrintChecksDialogOpen,
    printChecksData,
    isPrintSuccessDialogOpen,
    isPrintFailureDialogOpen,
    queryClient
  ]);

  const handleCloseDialog = () => {
    setIsPrintFailureDialogOpen(false);
    dispatch(setIsPrintFailureDialogOpen(false));
    dispatch(clearCheckWritingResponse());
  };

  useEffect(() => {
    if (!isWorkflowPending) dispatch(setPrintChecksData([]));
  }, [dispatch, isWorkflowPending]);

  useEffect(() => {
    if (amplitude && !trackLaunchOfPrintChecks) {
      amplitudeTracker(amplitude, EVENT_NAME.PrintChecksTab);
      setTrackLaunchOfPrintChecks(true);
    }
  }, [amplitude, trackLaunchOfPrintChecks, setTrackLaunchOfPrintChecks]);

  return (
    <>
      <ScrollToTopButton />
      {landingPageFlag === true ? (
        <HighVolumePrintChecksTable />
      ) : (
        <PrintChecksTable />
      )}
      <PrintChecksDialog open={isPrintChecksDialogOpen} />
      <PrintSuccessDialog open={isPrintSuccessDialogOpen} />
      <PrintFailureDialog
        open={isPrintFailureDialogOpen}
        close={handleCloseDialog}
      />
    </>
  );
};
